import {
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

import AuthGuard from './components/AuthGuard';
import Layout from './components/Layout';
import B2bDashboard from './components/B2bDashboard';
import UserManagement from './components/users/UserManagement';
import UserDetailView from './components/users/UserDetailView';
import SpaceManagement from './components/spaces/SpaceManagement';
import SpaceDetailView from './components/spaces/SpaceDetailView';
import SpaceMemberList from './components/spaces/SpaceMemberList';
import AdminManagement from './components/admins/AdminManagement';
import AdminDetailView from './components/admins/AdminDetailView';
import ReportManagement from './components/ReportManagement';
import FeedbackManagement from './components/FeedbackManagement';
import AuditLogs from './components/AuditLogs';
import PlatformConfig from './components/PlatformConfig';
import MyAccount from './components/MyAccount';
import ChatroomDetail from './components/ChatroomDetail';
import ChatMemberList from './components/ChatMemberList';
import ChatHistory from './components/ChatHistory';
import { ICurrentUser } from './contexts/AuthContext';

interface IAppRoutesProps {
  currentUser: ICurrentUser;
  onLogout: () => void;
  onUserUpdated?: (user: ICurrentUser) => void;
}

interface ILayoutWrapperProps {
  currentUser: ICurrentUser;
  onLogout: () => void;
}

const LayoutWrapper = ({ currentUser, onLogout }: ILayoutWrapperProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  const activeTab =
    location.pathname.replace(/^\//, '').split('/')[0] || 'dashboard';

  const handleTabChange = (tab: string) => {
    navigate(`/${tab}`);
  };

  const layoutUser = {
    username: currentUser.username,
    displayName: currentUser.displayName,
    avatar: currentUser.avatar ?? undefined,
    isOwner: currentUser.isOwner,
    permissions: currentUser.permissions,
  };

  return (
    <Layout
      user={layoutUser}
      onLogout={onLogout}
      activeTab={activeTab}
      onTabChange={handleTabChange}
    >
      <Outlet />
    </Layout>
  );
};

const ReportManagementRoute = () => {
  const navigate = useNavigate();

  return (
    <ReportManagement
      onNavigateUser={(userId: string) => navigate(`/users/${userId}`)}
    />
  );
};

const FeedbackManagementRoute = () => {
  const navigate = useNavigate();

  return (
    <FeedbackManagement
      onNavigateDetail={(id: string) => navigate(`/feedback/${id}`)}
      onBackToList={() => navigate('/feedback')}
    />
  );
};

const FeedbackDetailRoute = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  return (
    <FeedbackManagement
      initialFeedbackId={id}
      onNavigateDetail={(feedbackId: string) =>
        navigate(`/feedback/${feedbackId}`)
      }
      onBackToList={() => navigate('/feedback')}
    />
  );
};

const PlatformConfigRoute = () => {
  const { tab } = useParams<{ tab: string }>();
  const navigate = useNavigate();

  return (
    <PlatformConfig
      initialTab={tab}
      onTabChange={(t: string) => navigate(`/platform-config/${t}`)}
    />
  );
};

const ChatroomDetailRoute = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  if (!id) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <ChatroomDetail
      roomId={id}
      onBack={(spaceId?: string) => {
        if (spaceId) {
          navigate(`/spaces/${spaceId}`);
        } else {
          navigate('/spaces');
        }
      }}
      onViewMembers={(roomId: string) => navigate(`/room-members/${roomId}`)}
      onViewMessages={(roomId: string) => navigate(`/room-messages/${roomId}`)}
    />
  );
};

const ChatMemberListRoute = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  if (!id) {
    return <Navigate to="/dashboard" replace />;
  }

  return <ChatMemberList roomId={id} onBack={() => navigate(-1)} />;
};

const ChatHistoryRoute = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  if (!id) {
    return <Navigate to="/dashboard" replace />;
  }

  return <ChatHistory roomId={id} onBack={() => navigate(-1)} />;
};

const AppRoutes = ({
  currentUser,
  onLogout,
  onUserUpdated,
}: IAppRoutesProps) => (
  <Routes>
    <Route
      element={<LayoutWrapper currentUser={currentUser} onLogout={onLogout} />}
    >
      <Route path="dashboard" element={<B2bDashboard />} />
      <Route
        path="users"
        element={
          <AuthGuard permission="users">
            <UserManagement />
          </AuthGuard>
        }
      />
      <Route
        path="users/:id"
        element={
          <AuthGuard permission="users">
            <UserDetailView />
          </AuthGuard>
        }
      />
      <Route
        path="spaces"
        element={
          <AuthGuard permission="spaces">
            <SpaceManagement />
          </AuthGuard>
        }
      />
      <Route
        path="spaces/:id"
        element={
          <AuthGuard permission="spaces">
            <SpaceDetailView />
          </AuthGuard>
        }
      />
      <Route
        path="spaces/:id/members"
        element={
          <AuthGuard permission="spaces">
            <SpaceMemberList />
          </AuthGuard>
        }
      />
      <Route
        path="reports"
        element={
          <AuthGuard permission="reports">
            <ReportManagementRoute />
          </AuthGuard>
        }
      />
      <Route
        path="feedback"
        element={
          <AuthGuard permission="feedback">
            <FeedbackManagementRoute />
          </AuthGuard>
        }
      />
      <Route
        path="feedback/:id"
        element={
          <AuthGuard permission="feedback">
            <FeedbackDetailRoute />
          </AuthGuard>
        }
      />
      <Route
        path="audit-logs"
        element={
          <AuthGuard permission="audit_logs">
            <AuditLogs />
          </AuthGuard>
        }
      />
      <Route
        path="admin-management"
        element={
          <AuthGuard permission="admins">
            <AdminManagement />
          </AuthGuard>
        }
      />
      <Route
        path="admin-management/:id"
        element={
          <AuthGuard permission="admins">
            <AdminDetailView />
          </AuthGuard>
        }
      />
      <Route
        path="platform-config"
        element={
          <AuthGuard permission="settings">
            <PlatformConfigRoute />
          </AuthGuard>
        }
      />
      <Route
        path="platform-config/:tab"
        element={
          <AuthGuard permission="settings">
            <PlatformConfigRoute />
          </AuthGuard>
        }
      />
      <Route
        path="my-account"
        element={
          <MyAccount
            currentUser={currentUser}
            onUserUpdated={onUserUpdated || (() => {})}
          />
        }
      />
      <Route
        path="room-detail/:id"
        element={
          <AuthGuard permission="spaces">
            <ChatroomDetailRoute />
          </AuthGuard>
        }
      />
      <Route
        path="room-members/:id"
        element={
          <AuthGuard permission="spaces">
            <ChatMemberListRoute />
          </AuthGuard>
        }
      />
      <Route
        path="room-messages/:id"
        element={
          <AuthGuard permission="spaces">
            <ChatHistoryRoute />
          </AuthGuard>
        }
      />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Route>
  </Routes>
);

export default AppRoutes;
